import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import LineChart from "../ui/LineChart";

const PlanningChart = () => {
  const [chains, setChains] = useState([]);
  const [selectedChain, setSelectedChain] = useState("");
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [lineChartData, setLineChartData] = useState({
    labels: [],
    datasets: [{ data: [] }, { data: [] }],
  });
  const [error, setError] = useState(null);

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      x: { title: { display: false, text: "Date" } },
      y: { title: { display: true, text: "Production" }, beginAtZero: true },
    },
  };

  const handleChainChange = (event) => {
    setSelectedChain(event.target.value);
    setModels([]);
    setSelectedModel("");
    setLineChartData({ labels: [], datasets: [{ data: [] }, { data: [] }] });
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
    if (event.target.value && selectedChain) {
      fetchPlanningData(event.target.value, selectedChain);
    }
  };

  useEffect(() => {
    const fetchChains = async () => {
      try {
        const response = await api.get("/chains");
        console.log("✅ Chains fetched:", response.data);
        setChains(response.data);
      } catch (error) {
        console.error("❌ Error fetching chains:", error);
      }
    };

    fetchChains();
  }, []);

  useEffect(() => {
    const fetchModelsByChain = async () => {
      if (!selectedChain) return;
      try {
        const response = await api.get(`/models-by-chain/${selectedChain}`);
        const chainModels = response.data || [];
        const latestModel = chainModels[0] || null;
        setModels(chainModels);
        setSelectedModel(latestModel ? String(latestModel.id) : "");
        if (latestModel) fetchPlanningData(latestModel.id, selectedChain);
        else setError("Aucun modèle trouvé pour cette chaîne");
      } catch (error) {
        setModels([]);
        setSelectedModel("");
        setError(error.response?.data?.message || "Impossible de charger les modèles de cette chaîne");
      }
    };

    fetchModelsByChain();
  }, [selectedChain]);

  const fetchPlanningData = async (modelId, chainId) => {
    setError(null);
    try {
      // Get planning data for this model and chain
      const planningResponse = await api.get(`/product_plans_model_dash_chain/${modelId}/${chainId}`);
      const productionResponse = await api.get(`/export_total/${modelId}`, {
        params: { chain_id: chainId },
      });
  
      const planningData = planningResponse.data.planning || {};
      const productionData = productionResponse.data.planning || {};
      
      console.log("🔍 PLANNING CHART DEBUG");
      console.table({
        modelId,
        chainId: chainId,
        planningResponse: planningResponse.data,
        productionResponse: productionResponse.data,
        totalChains: planningResponse.data.total_chains,
        chains: planningResponse.data.chains,
        startDate: planningResponse.data.start_date,
        endDate: planningResponse.data.end_date
      });
      
      // Use daily totals logic with weekend filtering
      const allLabels = Array.from(new Set([
        ...Object.keys(planningData),
        ...Object.keys(productionData),
      ])).sort();
      
      // Filter out weekends with 0 production
      const filteredLabels = allLabels.filter((day) => {
        const plannedEntries = planningData[day];
        const productionEntries = productionData[day];
        
        const plannedValue = Array.isArray(plannedEntries)
          ? plannedEntries.reduce((sum, e) => sum + (e.models_finished || 0), 0)
          : plannedEntries ?? 0;
          
        const productionValue = Array.isArray(productionEntries)
          ? productionEntries.reduce((sum, e) => sum + (e.cumulative ?? 0), 0)
          : typeof productionEntries === "number" ? productionEntries : 0;
          
        const dayOfWeek = new Date(`${day}T00:00:00`).getDay(); // 0 = Sunday, 6 = Saturday
        
        // Filter out Saturday (6) and Sunday (0) if both productions are 0
        if ((dayOfWeek === 0 || dayOfWeek === 6) && plannedValue === 0 && productionValue === 0) {
          return false;
        }
        return true;
      });
      
      const planingData = filteredLabels.map((day) => {
          const entries = planningData[day];
          return Array.isArray(entries)
            ? entries.reduce((sum, e) => sum + (e.models_finished || 0), 0)
            : entries ?? 0;
        });
  
      const productionEData = filteredLabels.map((day) => {
          const entries = productionData[day];
          if (Array.isArray(entries)) {
            return entries.reduce((sum, e) => sum + (e.cumulative ?? 0), 0);
          } else if (typeof entries === "number") {
            return entries;
          } else {
            return 0;
          }
        });
          
      console.log("📅 Final Daily Labels:", filteredLabels);
        console.log("📅 Daily Planning Totals:", planingData);
        console.log("📅 Daily Production Totals:", productionEData);
  
        setLineChartData({
        labels: filteredLabels,
          datasets: [
            {
              label: "Planning",
              data: planingData,
              borderColor: "rgba(75,192,192,1)",
              backgroundColor: "rgba(75,192,192,0.2)",
            },
            {
              label: "Production Réelle",
              data: productionEData,
              borderColor: "rgba(153,102,255,1)",
              backgroundColor: "rgba(153,102,255,0.2)",
            },
          ],
        });
    } catch (error) {
      console.error("❌ Error in fetchPlanningData:", error);
      setError(error.response?.data?.message || "An error occurred");
    }
  };
      
  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Suivi de production en graphique</h2>
          <p className="text-gray-600 mt-1">Suivi la production planifiée vs réelle dans le temps</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner une Chaîne
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white shadow-sm"
              value={selectedChain}
              onChange={handleChainChange}
            >
              <option value="">Choisir une chaîne...</option>
              {chains &&
                chains.map((chain) => (
                  <option key={chain.id} value={chain.id}>
                    {chain.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="w-full sm:w-56">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner un Modèle
            </label>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white shadow-sm"
              value={selectedModel}
              onChange={handleModelChange}
              disabled={!selectedChain || models.length === 0}
            >
              <option value="">Choisir un modèle...</option>
              {models.map((model, index) => (
                <option key={model.id} value={model.id}>
                  {model.modele}{index === 0 ? " — Plus récent" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <LineChart 
            data={lineChartData} 
            options={lineChartOptions}
            title="Chronologie de Production"
            subtitle="Comparaison des quantités de production planifiées vs réelles"
          />
        </div>
      )}

    </div>
  );
};

export default PlanningChart;
